import { get, off, onValue, ref, set } from "firebase/database";
import { db } from "../lib/firebase";
import { getPathDatabaseByRole } from "./auth";
import { LaunchError } from "../utils/errors";
import { startUploadFileAws } from "../services/public";
import { canWriteCreativeTemplates } from "../features/creativeTemplates/utils/permissions";
import {
  getFontMimeType,
  mergeFontCatalogs,
  normalizeFontName,
  toFontKey,
  validateFontName,
} from "../features/creativeTemplates/utils/fonts";

const getCommonFontsPath = () => "fonts";

const getAdvertiserFontsPath = ({ advertiserID }) =>
  `fontsByAdvertiser/${advertiserID}`;

const getAdvertiserFontPath = ({ advertiserID, fontKey }) =>
  `${getAdvertiserFontsPath({ advertiserID })}/${fontKey}`;

const getFontUploadPath = ({ advertiserID }) =>
  `creative-fonts/${advertiserID}/`;

export const startListenerMergedFonts =
  ({
    onUpdatedDataCallback = () => {},
    onReferenceAvailable = () => {},
  } = {}) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      const commonRef = ref(db, getCommonFontsPath());
      const advertiserRef = advertiserID
        ? ref(db, getAdvertiserFontsPath({ advertiserID }))
        : null;

      let commonFonts = {};
      let advertiserFonts = {};

      const emit = () => {
        onUpdatedDataCallback(
          mergeFontCatalogs({ commonFonts, advertiserFonts })
        );
      };

      onReferenceAvailable({ commonRef, advertiserRef });

      onValue(
        commonRef,
        (snapshot) => {
          commonFonts = snapshot.val() || {};
          emit();
        },
        () => {
          commonFonts = {};
          emit();
        }
      );

      if (advertiserRef) {
        onValue(
          advertiserRef,
          (snapshot) => {
            advertiserFonts = snapshot.val() || {};
            emit();
          },
          () => {
            advertiserFonts = {};
            emit();
          }
        );
      } else {
        emit();
      }

      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerMergedFonts",
          tags: { action: "fonts" },
          level: "error",
        })
      );
      onUpdatedDataCallback([]);
      return false;
    }
  };

export const stopListenerMergedFonts =
  ({ listenerRefs } = {}) =>
  async () => {
    if (listenerRefs?.commonRef) {
      await off(listenerRefs.commonRef);
    }
    if (listenerRefs?.advertiserRef) {
      await off(listenerRefs.advertiserRef);
    }
  };

export const startUploadAdvertiserFont =
  ({ name, file } = {}) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID, role } = dispatch(getPathDatabaseByRole());
      if (!canWriteCreativeTemplates({ role })) {
        return { ok: false, reason: "unauthorized" };
      }

      const fontName = normalizeFontName({ name });
      const nameError = validateFontName({ name: fontName });
      if (nameError) {
        return { ok: false, reason: nameError };
      }
      const fontKey = toFontKey({ name: fontName });

      const mimeType = getFontMimeType({ file });
      if (!mimeType) {
        return { ok: false, reason: "invalidFile" };
      }
      if (!advertiserID || !file) {
        return { ok: false, reason: "missingParameters" };
      }

      const url = await dispatch(
        startUploadFileAws(
          file,
          mimeType,
          getFontUploadPath({ advertiserID }),
          true,
          true
        )
      );
      if (!url) {
        return { ok: false, reason: "uploadFailed" };
      }

      const fontRef = ref(
        db,
        getAdvertiserFontPath({ advertiserID, fontKey })
      );
      const existing = (await get(fontRef)).val() || {};
      const now = Date.now();

      await set(fontRef, {
        key: fontKey,
        name: fontName,
        url,
        mimeType,
        creationTime: existing.creationTime || now,
        updateTime: now,
      });

      return {
        ok: true,
        data: { key: fontKey, name: fontName, url, mimeType },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startUploadAdvertiserFont",
          tags: { action: "fonts" },
          level: "error",
        })
      );
      return { ok: false, reason: "uploadFailed" };
    }
  };
